import { useNavigate } from "react-router";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Back-navigation button that pops one entry off the router history.
 * Lives in the shared app header so every page has a consistent way to go
 * back — important for app-mode users who have no browser navigation controls.
 * @param className - Optional extra classes merged onto the button.
 * @param onClick - Optional handler; defaults to popping one router history
 * entry, or navigating to /inbox when there is no prior in-app entry.
 * @returns A ghost icon button that navigates back one step in history.
 */
export function BackButton({
  className,
  onClick,
}: {
  className?: string;
  onClick?: () => void;
}) {
  const navigate = useNavigate();
  const canGoBack = (window.history.state?.idx ?? 0) > 0;
  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn("group h-7 w-7 text-muted-foreground", className)}
      onClick={
        onClick ?? (() => (canGoBack ? navigate(-1) : navigate("/inbox")))
      }
      aria-label="Go back"
    >
      <ChevronLeft className="h-4 w-4 transition-transform duration-200 ease-out group-hover:-translate-x-0.5 motion-reduce:transition-none" />
    </Button>
  );
}
